import React from 'react';
import { useNavigate } from 'react-router-dom';

const TestCard = ({ 
  testData,
  index = 0,
  isClickable = false,
  isReportReady = false,
  onClick = null
}) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (isClickable && testData.exam_id) {
      navigate(`/myresult/${testData.exam_id}`);
    }
  };

  // Format date consistently
  const formatDate = (date) => {
    if (!date) return '2025. 9. 8. 14:26';
    
    return new Date(date).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Calculate accuracy percentage
  const calculateAccuracy = () => {
    const correct = testData.correct_count || 0;
    const wrong = testData.wrong_count || 0;
    const total = correct + wrong;
    return total > 0 ? Math.round((correct / total) * 100) : 0;
  };

  // Format difficulty
  const formatDifficulty = () => {
    if (testData.average_difficulty !== undefined) {
      return testData.average_difficulty.toFixed(2);
    } else if (testData.difficulty !== undefined) {
      return (testData.difficulty / 100).toFixed(2);
    }
    return '1.00';
  };

  // Get difficulty level text based on difficulty value
  const getDifficultyLevel = () => {
    const difficulty = parseFloat(formatDifficulty());
    if (difficulty < 1.5) return '하';
    if (difficulty < 2.5) return '중하';
    if (difficulty < 3.5) return '중';
    if (difficulty < 4.5) return '중상';
    return '상';
  };

  return (
    <div
      className={`bg-white rounded-3xl p-4 shadow-sm ${
        isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
      } ${testData.status === 200 ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)' }}
      onClick={handleClick}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-[14px] font-semibold text-[#231F20]">
              {testData.tree_name || '공통수학1(2022)'}
            </p>
            {testData.exam_type && (
              <span className="w-[38px] h-[16px] flex items-center justify-center bg-[#4ABC85] text-[#FFFFFF] rounded-full text-[8px]">
                {testData.exam_type}
              </span>
            )}
            {/* 리포트 상태 배지 */}
            {isReportReady ? (
              <span className="w-[38px] h-[16px] flex items-center justify-center bg-[#4ABC85] text-[#FFFFFF] rounded-full text-[8px]">
                리포트
              </span>
            ) : (
              <span className="w-[38px] h-[16px] flex items-center justify-center bg-[#A3A1A2] text-[#FFFFFF] rounded-full text-[8px]">
                리포트
              </span>
            )}
          </div>
          <p className="text-[12px] text-[#6D6B6C]">
            {testData.edu_ver || '고등학교 1학년'}
          </p>
        </div>
        <p className="text-[12px] text-[#A3A1A2]">
          {formatDate(testData.updated_at || testData.start_time)}
        </p>
      </div>
      
      {/* 4개 통계 카드 그리드 */}
      <div className="grid grid-cols-4 gap-2">
        {/* TID 카드 */}
        <div className="bg-[#F5F5F5] rounded-3xl p-2 text-center">
          <div className="text-[9px] text-[#6D6B6C]">TID</div>
          <div className="text-[13px] font-bold text-[#231F20]">
            {testData.exam_id || 261}
          </div>
          <div className="text-[8px] text-[#A3A1A2]">
            ({testData.exam_count || index + 1}회차)
          </div>
        </div>
        
        {/* 난이도 카드 */}
        <div className="bg-[#F5F5F5] rounded-3xl p-2 text-center">
          <div className="text-[9px] text-[#6D6B6C]">난이도</div>
          <div className="text-[13px] font-bold text-[#231F20]">
            {formatDifficulty()}
          </div>
          <div className="text-[8px] text-[#A3A1A2]">
            ({getDifficultyLevel()})
          </div>
        </div>
        
        {/* 정답률 카드 */}
        <div className="bg-[#F5F5F5] rounded-3xl p-2 text-center">
          <div className="text-[9px] text-[#6D6B6C]">정답률</div>
          <div className="text-[13px] font-bold text-[#231F20]">
            {calculateAccuracy()}%
          </div>
          <div className="text-[8px]">
            <span className="text-[#A3A1A2]">(</span>
            <span className="text-[#4ABC85] font-bold">{testData.correct_count || 0}</span>
            <span className="text-[#A3A1A2]">/</span>
            <span className="text-[#EF4444] font-bold">{testData.wrong_count || 0}</span>
            <span className="text-[#A3A1A2]">/</span>
            <span className="text-[#231F20] font-bold">
              {(testData.correct_count || 0) + (testData.wrong_count || 0) || testData.problem_count || 25}
            </span>
            <span className="text-[#A3A1A2]">)</span>
          </div>
        </div>
        
        {/* 폼레이팅 카드 */}
        <div className="bg-[#F5F5F5] rounded-3xl p-2 text-center">
          <div className="text-[9px] text-[#6D6B6C]">폼레이팅</div>
          <div className="text-[13px] font-bold">
            {(() => {
              const rating = (testData.rating || 0).toString();
              const length = rating.length;

              if (length >= 5) {
                // 5자리 이상: 앞 3자리 녹색, 나머지 회색
                const firstThree = rating.slice(0, 3);
                const rest = rating.slice(3);
                return (
                  <>
                    <span className="text-[#4ABC85]">{firstThree}</span>
                    <span className="text-[#A3A1A2]">{rest}</span>
                  </>
                );
              } else if (length === 4) {
                // 4자리: 앞 2자리 녹색, 뒤 2자리 회색
                const firstTwo = rating.slice(0, 2);
                const lastTwo = rating.slice(2);
                return (
                  <>
                    <span className="text-[#4ABC85]">{firstTwo}</span>
                    <span className="text-[#A3A1A2]">{lastTwo}</span>
                  </>
                );
              } else if (length === 3) {
                // 3자리: 앞 1자리 녹색, 뒤 2자리 회색
                const first = rating.slice(0, 1);
                const lastTwo = rating.slice(1);
                return (
                  <>
                    <span className="text-[#4ABC85]">{first}</span>
                    <span className="text-[#A3A1A2]">{lastTwo}</span>
                  </>
                );
              } else {
                // 1-2자리: 모두 녹색
                return <span className="text-[#4ABC85]">{rating}</span>;
              }
            })()}
          </div>
          <div className="text-[8px] text-[#A3A1A2]">
            {testData.delta !== undefined && testData.delta !== 0 ? (
              <>
                <span className="text-[#A3A1A2]">(</span>
                <span className={`${testData.delta > 0 ? 'text-[#4ABC85]' : 'text-[#EF4444]'}`}>
                  {testData.delta > 0 ? '+' : ''}{testData.delta} {testData.delta > 0 ? '상승' : '하락'}
                </span>
                <span className="text-[#A3A1A2]">)</span>
              </>
            ) : <span className="text-[#A3A1A2]">(유지)</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCard;